/**
 * Timesheet — Keka-style category grid.
 *
 * Fixed rows in two groups:
 *   • Active Hours: Development / Learning / Travel / Consulting
 *   • Time Off:     Public Holiday / Casual / Sick / Privileged / Optional Holiday
 *
 * Each cell is one number of hours per (category, day). PUT on blur.
 * Leave balances render below the grid for reference.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import { DesktopOnly } from "@/components/DesktopOnly";
import { TimesheetGridView } from "@/components/TimesheetGridView";
import { api, downloadBlob, extractErrorMessage } from "@/lib/api";
import type {
  CellUpsert,
  LeaveBalance,
  LeaveType,
  TimesheetGrid,
  TimesheetSubmission,
} from "@/lib/types";

type View = "monthly" | "weekly" | "custom";

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfWeek(d: Date): Date {
  const day = d.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + mondayOffset);
}
function endOfWeek(d: Date): Date {
  const s = startOfWeek(d);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
}
function fmtISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function parseISO(s: string): Date {
  // Local-midnight so a yyyy-mm-dd string doesn't shift by timezone.
  return new Date(`${s}T00:00:00`);
}
function fmtRangeLabel(from: Date, to: Date): string {
  const sameYear = from.getFullYear() === to.getFullYear();
  const fmt: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  return `${from.toLocaleDateString(undefined, fmt)}${sameYear ? "" : ` ${from.getFullYear()}`} – ${to.toLocaleDateString(undefined, fmt)} ${to.getFullYear()}`;
}

/** Pull the unfilled-weekday list out of a 422 `incomplete_timesheet` error,
 * or null if the error is something else. */
function incompleteDays(err: unknown): string[] | null {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response
    ?.data?.detail;
  if (
    detail &&
    typeof detail === "object" &&
    (detail as { type?: string }).type === "incomplete_timesheet"
  ) {
    return (detail as { unfilled_days?: string[] }).unfilled_days ?? [];
  }
  return null;
}

function fmtDayLabel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function TimesheetPage() {
  // 9 category rows × 7 days makes a grid that just doesn't read on a
  // phone. Vertical accordion is a real follow-up; until then, gate.
  return (
    <DesktopOnly
      title="The timesheet"
      blurb="The weekly grid (9 categories × 7 days) needs more horizontal room than a phone screen offers."
    >
      <TimesheetPageImpl />
    </DesktopOnly>
  );
}

function TimesheetPageImpl() {
  const [view, setView] = useState<View>("monthly");
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  // Custom range (ISO yyyy-mm-dd) — defaults to the current month.
  const [customFrom, setCustomFrom] = useState<string>(() => fmtISO(startOfMonth(new Date())));
  const [customTo, setCustomTo] = useState<string>(() => fmtISO(endOfMonth(new Date())));
  const [banner, setBanner] = useState<
    { kind: "info" | "error"; text: string } | null
  >(null);
  // ISO weekday dates the employee left blank — drives the "fill these days" popup.
  const [unfilled, setUnfilled] = useState<string[] | null>(null);

  const { from, to } = useMemo(() => {
    if (view === "weekly") {
      return { from: startOfWeek(anchor), to: endOfWeek(anchor) };
    }
    if (view === "custom") {
      return { from: parseISO(customFrom), to: parseISO(customTo) };
    }
    return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
  }, [view, anchor, customFrom, customTo]);

  const fromISO = fmtISO(from);
  const toISO = fmtISO(to);
  // In monthly view, submission state isn't meaningful (a "week" submission
  // doesn't cleanly map to a calendar month). Only show submit UI in weekly.
  const weekStartISO = fmtISO(startOfWeek(anchor));

  const queryClient = useQueryClient();
  const gridQuery = useQuery({
    queryKey: ["timesheet-grid", fromISO, toISO],
    queryFn: async () =>
      (
        await api.get<TimesheetGrid>(
          `/time-entries/timesheet?from=${fromISO}&to=${toISO}`,
        )
      ).data,
    refetchOnWindowFocus: false,
  });

  // Submission status for the active week. Null = unsubmitted.
  // Auto-refetch on focus + a gentle poll so an admin's decision (reject /
  // approve / release) reflects without a manual refresh — otherwise a week the
  // employee has open stays visually "locked" even after the admin rejects it,
  // and they can't tell the cells are editable again.
  const submissionQuery = useQuery({
    queryKey: ["timesheet-submission", weekStartISO],
    queryFn: async () =>
      (
        await api.get<TimesheetSubmission | null>(
          `/time-entries/my-submission?week_start=${weekStartISO}`,
        )
      ).data,
    enabled: view === "weekly",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
  const submission = submissionQuery.data ?? null;
  const locked =
    submission?.status === "submitted" || submission?.status === "approved";

  // Holiday auto-fill — run once per (week, lock-state). Backend is idempotent
  // and skips days that already have any entry.
  const autofilledFor = useRef<string | null>(null);
  useEffect(() => {
    if (view !== "weekly") return;
    if (locked) return;
    const key = weekStartISO;
    if (autofilledFor.current === key) return;
    autofilledFor.current = key;
    api
      .post<{ filled: number }>(
        `/time-entries/autofill-holidays?week_start=${weekStartISO}`,
      )
      .then((resp) => {
        if (resp.data?.filled > 0) {
          queryClient.invalidateQueries({ queryKey: ["timesheet-grid"] });
        }
      })
      .catch(() => {
        /* non-fatal — calendar may be empty, etc. */
      });
  }, [view, weekStartISO, locked, queryClient]);

  const upsertCell = useMutation({
    mutationFn: async (payload: CellUpsert) =>
      (await api.put("/time-entries/cell", payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["timesheet-grid"] }),
    onError: (err) =>
      setBanner({ kind: "error", text: extractErrorMessage(err, "Save failed.") }),
  });

  const submitWeek = useMutation({
    mutationFn: async () =>
      (
        await api.post<TimesheetSubmission>("/time-entries/submit", {
          week_start: weekStartISO,
        })
      ).data,
    onSuccess: () => {
      setBanner({ kind: "info", text: "Week submitted for approval." });
      queryClient.invalidateQueries({ queryKey: ["timesheet-submission"] });
      queryClient.invalidateQueries({ queryKey: ["timesheet-grid"] });
    },
    onError: (err) => {
      const days = incompleteDays(err);
      if (days) {
        setUnfilled(days);
        return;
      }
      setBanner({
        kind: "error",
        text: extractErrorMessage(err, "Could not submit this week."),
      });
    },
  });

  const requestDownload = useMutation({
    mutationFn: async () =>
      (
        await api.post<TimesheetSubmission>(
          `/time-entries/request-download?week_start=${weekStartISO}`,
        )
      ).data,
    onSuccess: () => {
      setBanner({ kind: "info", text: "Download requested — your admin will release it." });
      queryClient.invalidateQueries({ queryKey: ["timesheet-submission"] });
    },
    onError: (err) =>
      setBanner({
        kind: "error",
        text: extractErrorMessage(err, "Could not request the download."),
      }),
  });

  const { data: balances = [] } = useQuery({
    queryKey: ["leave-balances", "me"],
    queryFn: async () => (await api.get<LeaveBalance[]>("/leave-balances/me")).data,
  });
  const { data: leaveTypes = [] } = useQuery({
    queryKey: ["leave-types"],
    queryFn: async () => (await api.get<LeaveType[]>("/leave-types")).data,
  });
  const typeById = useMemo(() => new Map(leaveTypes.map((t) => [t.id, t])), [leaveTypes]);

  const rows = gridQuery.data?.rows ?? [];
  const grandTotal = gridQuery.data?.grand_total_seconds ?? 0;

  // Period navigation — shared by the ‹ › buttons and the keyboard arrows.
  const goPrev = () =>
    setAnchor((d) =>
      view === "weekly"
        ? new Date(d.getFullYear(), d.getMonth(), d.getDate() - 7)
        : new Date(d.getFullYear(), d.getMonth() - 1, 1),
    );
  const goNext = () =>
    setAnchor((d) =>
      view === "weekly"
        ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7)
        : new Date(d.getFullYear(), d.getMonth() + 1, 1),
    );

  // Refresh = pull the grid AND the submission status AND leave balances, so a
  // click reflects anything an admin changed (approve / reject / modify / release
  // download). Refetching only the grid looked like "nothing happened".
  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["timesheet-grid"] });
    queryClient.invalidateQueries({ queryKey: ["timesheet-submission"] });
    queryClient.invalidateQueries({ queryKey: ["leave-balances", "me"] });
  };

  // ← / → move to the previous / next period, unless the user is typing in a
  // field (cell entry, search box) — then the arrows do their normal thing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      )
        return;
      // Custom range uses date inputs, not period stepping — arrows are inert.
      if (view === "custom") return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // goPrev/goNext only read `view` (anchor is updated functionally).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  return (
    <div>
      <header style={headerRow}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
            My time
          </h1>
          <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
            {fmtRangeLabel(from, to)} · {hms(grandTotal)} logged
          </p>
        </div>
      </header>

      <div style={gridToolbar}>
        {view === "custom" ? (
          <>
            <span style={fieldHint}>From</span>
            <input
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              style={dateInput}
              aria-label="Range start"
            />
            <span style={fieldHint}>To</span>
            <input
              type="date"
              value={customTo}
              min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              style={dateInput}
              aria-label="Range end"
            />
          </>
        ) : (
          <>
            <button
              type="button"
              aria-label="Previous period (← arrow)"
              title="Previous period (← arrow key)"
              style={navBtn}
              onClick={goPrev}
            >
              ‹
            </button>
            <span style={dateLabel}>{fmtRangeLabel(from, to)}</span>
            <button
              type="button"
              aria-label="Next period (→ arrow)"
              title="Next period (→ arrow key)"
              style={navBtn}
              onClick={goNext}
            >
              ›
            </button>
            <button type="button" onClick={() => setAnchor(new Date())} style={navBtn}>
              Today
            </button>
          </>
        )}
        <select
          value={view}
          onChange={(e) => setView(e.target.value as View)}
          style={{ ...navBtn, paddingRight: 20, cursor: "pointer" }}
        >
          <option value="monthly">Monthly</option>
          <option value="weekly">Weekly</option>
          <option value="custom">Custom</option>
        </select>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={refreshAll}
          style={navBtn}
          aria-label="Refresh"
          title="Refresh"
        >
          ↻
        </button>
      </div>

      {view === "weekly" && (
        <SubmissionBar
          submission={submission}
          locked={locked}
          pending={submitWeek.isPending}
          onSubmit={() => submitWeek.mutate()}
          onRequestDownload={() => requestDownload.mutate()}
          requestPending={requestDownload.isPending}
          banner={banner}
          onDismissBanner={() => setBanner(null)}
        />
      )}

      {view === "monthly" && (
        <MonthlyDownloadBar
          from={fromISO}
          to={toISO}
          label={fmtRangeLabel(from, to)}
          onIncomplete={setUnfilled}
        />
      )}

      {view === "custom" && (
        <MonthlyDownloadBar
          kind="range"
          from={fromISO}
          to={toISO}
          label={fmtRangeLabel(from, to)}
          onIncomplete={setUnfilled}
        />
      )}

      {view !== "weekly" && banner && (
        <button
          type="button"
          onClick={() => setBanner(null)}
          style={{
            display: "block",
            width: "100%",
            textAlign: "left",
            margin: "0 0 12px",
            padding: "8px 12px",
            fontSize: 13,
            color: banner.kind === "error" ? "#ee1d36" : "#047a18",
            background:
              banner.kind === "error"
                ? "rgba(238,29,54,0.06)"
                : "rgba(0,215,34,0.06)",
            border:
              banner.kind === "error"
                ? "1px solid rgba(238,29,54,0.3)"
                : "1px solid rgba(0,215,34,0.3)",
            borderRadius: 4,
            cursor: "pointer",
            fontFamily: "var(--font-sans)",
          }}
        >
          {banner.text}
        </button>
      )}

      <TimesheetGridView
        from={from}
        to={to}
        rows={rows}
        totalsByDay={gridQuery.data?.totals_by_day ?? {}}
        loading={gridQuery.isLoading}
        readOnly={view === "weekly" && locked}
        onCellChange={(category, workDate, hours) =>
          upsertCell.mutate({ category, work_date: workDate, hours })
        }
      />

      {unfilled && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setUnfilled(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(8,8,8,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--canvas)",
              border: "1px solid var(--hairline)",
              borderRadius: 8,
              padding: 24,
              maxWidth: 440,
              width: "100%",
              boxShadow: "0 10px 40px rgba(0,0,0,0.18)",
            }}
          >
            <h2 style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 600, color: "var(--ink)" }}>
              Fill every weekday before submitting
            </h2>
            <p style={{ margin: "0 0 14px", fontSize: 13.5, lineHeight: 1.5, color: "var(--body)" }}>
              {unfilled.length} weekday{unfilled.length === 1 ? "" : "s"}{" "}
              {unfilled.length === 1 ? "has" : "have"} no hours. Add working hours or
              time off for each — only Saturday and Sunday may be left blank.
            </p>
            <ul
              style={{
                margin: "0 0 18px",
                padding: 0,
                listStyle: "none",
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              {unfilled.map((iso) => (
                <li
                  key={iso}
                  style={{
                    fontSize: 12.5,
                    fontFamily: "var(--font-mono)",
                    background: "rgba(238,29,54,0.06)",
                    border: "1px solid rgba(238,29,54,0.3)",
                    color: "#ee1d36",
                    borderRadius: 4,
                    padding: "4px 8px",
                  }}
                >
                  {fmtDayLabel(iso)}
                </li>
              ))}
            </ul>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setUnfilled(null)}
                style={{
                  background: "var(--ink)",
                  color: "var(--on-primary)",
                  border: "none",
                  borderRadius: 4,
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {balances.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <div style={sectionTitle}>Leave balances · {new Date().getFullYear()}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {balances.map((b) => {
              const t = typeById.get(b.leave_type_id);
              const remaining =
                Number(b.allocated_days) - Number(b.used_days) - Number(b.pending_days);
              return (
                <div
                  key={b.id}
                  style={{
                    background: "var(--canvas)",
                    border: "1px solid var(--hairline)",
                    borderRadius: 8,
                    padding: 16,
                  }}
                >
                  <div style={{ fontSize: 12, color: "var(--mute)", marginBottom: 4 }}>
                    {t?.name ?? "Leave"}
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-0.2px" }}>
                    {remaining.toFixed(1)}
                    <span style={{ fontSize: 13, color: "var(--mute)", fontWeight: 400 }}>
                      {" "}
                      / {Number(b.allocated_days).toFixed(0)} d
                    </span>
                  </div>
                  {Number(b.pending_days) > 0 && (
                    <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 4 }}>
                      {Number(b.pending_days).toFixed(1)} pending
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

const headerRow: CSSProperties = {
  marginBottom: 16,
  display: "flex",
  alignItems: "baseline",
  gap: 16,
};

const gridToolbar: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  marginBottom: 8,
};

const navBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "5px 10px",
  fontSize: 12,
  color: "var(--ink)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const dateLabel: CSSProperties = {
  padding: "5px 14px",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  fontSize: 12.5,
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

const dateInput: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "4px 8px",
  fontSize: 12.5,
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

const fieldHint: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
};

const sectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 8,
};

function hms(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

// ---------- Monthly download / request bar ----------

function MonthlyDownloadBar({
  from,
  to,
  onIncomplete,
  kind = "month",
}: {
  from: string;
  to: string;
  label: string;
  onIncomplete: (days: string[]) => void;
  kind?: "month" | "range";
}) {
  // Copy switches between "month" and "selected range" so the same bar serves
  // the Monthly view and the Custom-date view.
  const noun = kind === "month" ? "month" : "range";
  const Noun = kind === "month" ? "Month" : "Range";
  const queryClient = useQueryClient();
  const [banner, setBanner] = useState<{ kind: "info" | "error"; text: string } | null>(null);

  const { data: subs = [] } = useQuery({
    queryKey: ["my-submissions", "monthbar"],
    queryFn: async () =>
      (await api.get<TimesheetSubmission[]>("/time-entries/my-submissions?limit=30")).data,
  });

  // Submissions whose week falls in the month being viewed (ISO strings sort
  // lexicographically, so range comparison is safe). The backend enforces the
  // authoritative gate; this just drives which control to show.
  const inRange = subs.filter((s) => s.week_start >= from && s.week_start <= to);
  const approved = inRange.filter((s) => s.status === "approved");
  const released = approved.filter((s) => s.download_approved);
  const pending = approved.filter((s) => !s.download_approved);
  const allReleased = approved.length > 0 && pending.length === 0;
  const submittedCount = inRange.filter(
    (s) => s.status === "submitted" || s.status === "approved",
  ).length;

  const submitMonth = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          submitted_weeks: number;
          skipped_empty: number;
          skipped_locked: number;
        }>(`/time-entries/submit-month?from=${from}&to=${to}`)
      ).data,
    onSuccess: (d) => {
      const parts = [`Submitted ${d.submitted_weeks} week(s) for approval.`];
      if (d.skipped_locked) parts.push(`${d.skipped_locked} already approved.`);
      if (d.skipped_empty) parts.push(`${d.skipped_empty} empty week(s) skipped.`);
      setBanner({ kind: "info", text: parts.join(" ") });
      queryClient.invalidateQueries({ queryKey: ["my-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["timesheet-submission"] });
      queryClient.invalidateQueries({ queryKey: ["timesheet-grid"] });
    },
    onError: (err) => {
      const days = incompleteDays(err);
      if (days) {
        onIncomplete(days);
        return;
      }
      setBanner({
        kind: "error",
        text: extractErrorMessage(err, `Could not submit the ${noun}.`),
      });
    },
  });

  const requestMonth = useMutation({
    mutationFn: async () =>
      (await api.post<{ requested_weeks: number }>(
        `/time-entries/request-download?from=${from}&to=${to}`,
      )).data,
    onSuccess: (d) => {
      setBanner({
        kind: "info",
        text: `Requested download for ${d?.requested_weeks ?? 0} week(s). Your admin will release them.`,
      });
      queryClient.invalidateQueries({ queryKey: ["my-submissions"] });
    },
    onError: (err) =>
      setBanner({
        kind: "error",
        text: extractErrorMessage(err, `Could not request the ${noun} download.`),
      }),
  });

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ ...monthBar, marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: "var(--body)", flex: 1 }}>
          {submittedCount > 0
            ? `${submittedCount} week(s) in this ${noun} submitted for approval. Submit again to push any newly-filled weeks.`
            : `Submit the whole ${noun} for approval in one click — your admin reviews each week.`}
        </span>
        <button
          type="button"
          style={{
            background: "var(--ink)",
            color: "var(--on-primary)",
            border: "none",
            borderRadius: 4,
            padding: "6px 14px",
            fontSize: 13,
            fontWeight: 500,
            cursor: submitMonth.isPending ? "wait" : "pointer",
            opacity: submitMonth.isPending ? 0.6 : 1,
          }}
          disabled={submitMonth.isPending}
          onClick={() => submitMonth.mutate()}
        >
          {submitMonth.isPending ? "Submitting…" : `Submit ${noun}`}
        </button>
      </div>
      <div style={monthBar}>
        <span style={{ fontSize: 12, color: "var(--body)", flex: 1 }}>
          {approved.length === 0
            ? `${Noun} download opens once weeks are approved and released by your admin.`
            : allReleased
              ? `All ${approved.length} approved week(s) in this ${noun} are released — download the full ${noun}.`
              : `${released.length}/${approved.length} approved week(s) released. Request the rest to download the whole ${noun}.`}
        </span>
        {allReleased ? (
          <>
            <button
              type="button"
              style={empDownloadBtn}
              title="Download Excel"
              onClick={() =>
                downloadBlob(
                  `/time-entries/export.xlsx?from=${from}&to=${to}`,
                  `timesheet-${from}-to-${to}.xlsx`,
                )
              }
            >
              ⇣ Excel
            </button>
            <button
              type="button"
              style={empDownloadBtn}
              title="Download PDF"
              onClick={() =>
                downloadBlob(
                  `/time-entries/export.pdf?from=${from}&to=${to}`,
                  `timesheet-${from}-to-${to}.pdf`,
                )
              }
            >
              ⇣ PDF
            </button>
          </>
        ) : approved.length > 0 ? (
          <button
            type="button"
            style={empDownloadBtn}
            disabled={requestMonth.isPending}
            onClick={() => requestMonth.mutate()}
          >
            {requestMonth.isPending ? "Requesting…" : `Request ${noun} download (${pending.length})`}
          </button>
        ) : null}
      </div>
      {banner && (
        <p
          style={{
            margin: "6px 0 0",
            fontSize: 12,
            color: banner.kind === "error" ? "#ee1d36" : "var(--mute)",
          }}
        >
          {banner.text}
        </p>
      )}
    </div>
  );
}

// ---------- Submission status bar ----------

function SubmissionBar({
  submission,
  locked,
  pending,
  onSubmit,
  onRequestDownload,
  requestPending,
  banner,
  onDismissBanner,
}: {
  submission: TimesheetSubmission | null;
  locked: boolean;
  pending: boolean;
  onSubmit: () => void;
  onRequestDownload: () => void;
  requestPending: boolean;
  banner: { kind: "info" | "error"; text: string } | null;
  onDismissBanner: () => void;
}) {
  const status = submission?.status ?? "draft";
  const adminModifiedBy = submission?.admin_modified_by_display_name;
  const adminModifiedAt = submission?.admin_modified_at;

  const palette = {
    draft: { fg: "var(--mute)", bg: "var(--surface-1)", border: "var(--hairline)" },
    submitted: { fg: "#8a5b00", bg: "rgba(255,174,19,0.08)", border: "rgba(255,174,19,0.4)" },
    approved: { fg: "#047a18", bg: "rgba(0,215,34,0.08)", border: "rgba(0,215,34,0.4)" },
    rejected: { fg: "#ee1d36", bg: "rgba(238,29,54,0.06)", border: "rgba(238,29,54,0.4)" },
  }[status] ?? { fg: "var(--mute)", bg: "var(--surface-1)", border: "var(--hairline)" };

  const buttonLabel =
    status === "rejected" ? "Resubmit week" : status === "submitted" ? "Submitted" : "Submit week";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginBottom: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 14px",
          border: `1px solid ${palette.border}`,
          borderRadius: 4,
          background: palette.bg,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: palette.fg,
          }}
        >
          {status === "draft" ? "Not submitted" : status}
        </span>
        <span style={{ fontSize: 12, color: "var(--body)", flex: 1 }}>
          {status === "submitted" && (
            <>
              Waiting on admin approval. Cells are locked until they decide.
              {submission?.active_seconds != null
                ? ` Active (billable) ${hms(submission.active_seconds)} of ${hms(submission.total_seconds)} total.`
                : ""}
            </>
          )}
          {status === "approved" && submission && (
            <>
              Approved
              {submission.manager_name
                ? ` by ${submission.manager_name}`
                : submission.decided_by_display_name
                  ? ` by ${submission.decided_by_display_name}`
                  : ""}
              {submission.active_seconds != null
                ? ` · active ${hms(submission.active_seconds)} of ${hms(submission.total_seconds)} total`
                : ""}
              {submission.hourly_rate_usd && submission.earnings_usd
                ? ` · $${Number(submission.hourly_rate_usd).toFixed(2)}/hr · earnings $${Number(submission.earnings_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : ""}
              . This week is locked.
            </>
          )}
          {status === "rejected" && submission && (
            <>
              Rejected
              {submission.decided_by_display_name ? ` by ${submission.decided_by_display_name}` : ""}
              {submission.decision_note ? ` — ${submission.decision_note}` : ""}. Edit and
              resubmit when ready.
            </>
          )}
          {status === "draft" && "Fill the week, then submit for admin approval."}
        </span>
        {/* Download is a second approval stage. Once the week is approved the
            employee asks for download access; the admin releases it, then the
            export buttons appear. */}
        {status === "approved" && submission && submission.download_approved && (
          <>
            <button
              type="button"
              onClick={() =>
                downloadBlob(
                  `/time-entries/export.xlsx?week_start=${submission.week_start}`,
                  `timesheet-week-${submission.week_start}.xlsx`,
                )
              }
              style={empDownloadBtn}
              title="Download Excel"
            >
              ⇣ Excel
            </button>
            <button
              type="button"
              onClick={() =>
                downloadBlob(
                  `/time-entries/export.pdf?week_start=${submission.week_start}`,
                  `timesheet-week-${submission.week_start}.pdf`,
                )
              }
              style={empDownloadBtn}
              title="Download PDF"
            >
              ⇣ PDF
            </button>
          </>
        )}
        {status === "approved" &&
          submission &&
          !submission.download_approved &&
          (submission.download_requested_at ? (
            <span style={{ fontSize: 12, color: "var(--mute)", whiteSpace: "nowrap" }}>
              Download requested
            </span>
          ) : (
            <button
              type="button"
              onClick={onRequestDownload}
              disabled={requestPending}
              style={empDownloadBtn}
              title="Ask your admin to release the download"
            >
              {requestPending ? "Requesting…" : "Request download"}
            </button>
          ))}
        <button
          type="button"
          onClick={onSubmit}
          disabled={pending || locked}
          style={{
            background: locked ? "var(--surface-2)" : "var(--ink)",
            color: locked ? "var(--mute)" : "var(--on-primary)",
            border: "none",
            borderRadius: 4,
            padding: "6px 14px",
            fontSize: 13,
            fontWeight: 500,
            cursor: pending ? "wait" : locked ? "not-allowed" : "pointer",
            opacity: pending ? 0.6 : 1,
          }}
        >
          {pending ? "Submitting…" : buttonLabel}
        </button>
      </div>
      {adminModifiedAt && (
        <div
          role="status"
          style={{
            padding: "8px 12px",
            fontSize: 13,
            color: "#5b2bcc",
            background: "rgba(122,61,255,0.06)",
            border: "1px solid rgba(122,61,255,0.3)",
            borderRadius: 4,
            lineHeight: 1.5,
          }}
        >
          <strong>Your admin modified this timesheet</strong>
          {adminModifiedBy ? ` (${adminModifiedBy})` : ""}
          {" — "}
          {new Date(adminModifiedAt).toLocaleString()}. The grid below shows the
          updated values.
        </div>
      )}
      {/* (see empDownloadBtn at file bottom) */}
      {banner && (
        <button
          type="button"
          onClick={onDismissBanner}
          style={{
            textAlign: "left",
            padding: "8px 12px",
            fontSize: 13,
            color: banner.kind === "error" ? "#ee1d36" : "#047a18",
            background:
              banner.kind === "error"
                ? "rgba(238,29,54,0.06)"
                : "rgba(0,215,34,0.06)",
            border:
              banner.kind === "error"
                ? "1px solid rgba(238,29,54,0.3)"
                : "1px solid rgba(0,215,34,0.3)",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          {banner.text}
        </button>
      )}
    </div>
  );
}

const empDownloadBtn: CSSProperties = {
  background: "var(--canvas)",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "5px 11px",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const monthBar: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  background: "var(--surface-1)",
};
